<template>
  <FreeToolPage
    title="Free online metronome"
    intro="A full-featured metronome that runs in your browser, with subdivisions, odd and compound time signatures, per-beat accents and five click sounds. No sign-up, no download, nothing to install."
    :features="features"
    :faq="faq"
    cta-title="Leading worship, not just practising?"
    cta-body="Song7 keeps this same metronome next to your chord charts, with each song's tempo and time signature already filled in, plus setlists, keys and transposition for the whole team."
  >
    <!--
      Client-only: the console needs an AudioContext and reads its saved
      settings from localStorage, neither of which exist while this page is
      being prerendered.
    -->
    <ClientOnly>
      <MetronomeConsole size="page" class="metronome-page__console" />

      <template #fallback>
        <v-skeleton-loader type="image" width="360" height="520" class="rounded-xl" />
      </template>
    </ClientOnly>

    <template #related>Need to tune up first? Try the <NuxtLink to="/tuner">free online tuner</NuxtLink>.</template>
  </FreeToolPage>
</template>

<script setup>
import { useSeo } from '~/composables/useSeo'

definePageMeta({
  layout: 'website',
})

const features = [
  {
    title: 'Subdivisions that actually swing',
    body: 'Practise against eighths, triplets, sixteenths or a swung offbeat. Subdivision clicks sit under the pulse so the beat stays readable by ear.',
  },
  {
    title: 'Odd and compound time',
    body: 'Anything from 1/4 to 16/16. 6/8, 9/8 and 12/8 are counted in dotted beats the way they are actually felt, and odd groupings like 3+3+2 come from accenting the beats you want.',
  },
  {
    title: 'A click that cuts through',
    body: 'Five synthesized sounds (click, ping, stick, wooden block and drum) with pitch and three-band EQ, so the click stays audible in in-ears next to a loud band.',
  },
]

const faq = [
  {
    question: 'Is this metronome really free?',
    answer:
      'Yes. The metronome runs entirely in your browser, needs no account, and has no time limit or feature lock. It is the same metronome built into the Song7 app.',
  },
  {
    question: 'Does it work offline?',
    answer:
      'Once the page has loaded it keeps working without a connection, because the click sounds are generated in the browser rather than downloaded. Only the optional shared-beat sync needs the network.',
  },
  {
    question: 'What does the sync option do?',
    answer:
      'It lines several devices up on the same downbeat. Each device measures its own clock offset against our server, then waits for the next shared bar before starting, so two phones in the same room count together instead of drifting apart.',
  },
  {
    question: 'How do I set an accent pattern?',
    answer:
      'Tap any beat dot to cycle it between accented, normal and silent. Patterns are remembered per time signature, so your 7/8 grouping is still there next time you pick 7/8.',
  },
  {
    question: 'Can I use it on my phone?',
    answer:
      'Yes. Turn the dial with your thumb to change tempo, or tap the middle of the dial in time to set it by ear. Keep the screen awake if you plan to leave it running through a whole song.',
  },
]

useSeo({
  keywords: [
    'online metronome',
    'free metronome',
    'metronome with subdivisions',
    'tap tempo',
    'metronome with accents',
    '6/8 metronome',
    'odd time signature metronome',
    'worship metronome',
  ],
  schema: 'WebApplication',
  faq,
})
</script>

<style scoped>
.metronome-page__console {
  width: 100%;
  max-width: 380px;
}
</style>
